import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import Form, { FormInstance } from "antd/es/form";
import { useWatch } from "antd/es/form/Form";
import { MenuProps } from "antd/es/menu";
import Tooltip from "antd/es/tooltip";
import { isEqual } from "lodash-es";
import { useIntl } from "react-intl";

import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import { EntityWithLocation } from "@mapmycustomers/shared/types/entity";
import GeoAddress from "@mapmycustomers/shared/types/GeoAddress";
import { isMapped } from "@mapmycustomers/shared/util/assert";
import { convertGeoPointToLatLngLiteral } from "@mapmycustomers/shared/util/geo/GeoService";
import { Alert, Popconfirm, TextField } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import { useMap } from "@app/component/map/FeaturedMap";
import { EnterPinLocationModePayload } from "@app/scene/map/store/pinLocation/actions";
import { reverseGeocodeAddress } from "@app/store/location/actions";
import geoAddressToAddress from "@app/util/geoAddressToAddress";
import floorToFixed from "@app/util/number/floorToFixed";
import isFloat from "@app/util/number/isFloat";
import useCoordinatesValidation, {
  areCoordinatesValid,
} from "@app/util/validation/useCoordinatesValidation";

import styles from "./AddressLabel.module.scss";
import messages from "./messages";

interface Props {
  coordinatesChanged: boolean;
  disabled?: boolean;
  entity: EntityWithLocation;
  form: FormInstance;
  formChanged: boolean;
  isMapMode: boolean;
  onChange?: (changed: boolean) => void;
  onEnterPinLocationMode?: (payload: EnterPinLocationModePayload) => void;
  onReverseGeocodeAddress: typeof reverseGeocodeAddress;
  onSetAddressChanged: (changed: boolean) => void;
  onSetCoordinatesChanged: (changed: boolean) => void;
  onShowChangePinLocationModal: () => void;
  required?: boolean;
}

const AddressLabel: React.FC<Props> = ({
  coordinatesChanged,
  disabled,
  entity,
  form,
  formChanged,
  isMapMode,
  onChange,
  onEnterPinLocationMode,
  onReverseGeocodeAddress,
  onSetAddressChanged,
  onSetCoordinatesChanged,
  onShowChangePinLocationModal,
  required,
}) => {
  const { map } = useMap();
  const hasLocation = isMapped(entity);
  const intl = useIntl();
  const [open, setOpen] = useState<boolean>(false);
  const addressChangedManually =
    form.isFieldTouched("addressDetails") && formChanged && !coordinatesChanged;
  const longitude: string | undefined =
    useWatch("longitude", form) ?? form.getFieldValue("longitude"); // Form Item is inside Popover, useWatch gives undefined till popover is opened.
  const latitude: string | undefined = useWatch("latitude", form) ?? form.getFieldValue("latitude");

  const addressDetails: GeoAddress =
    useWatch("addressDetails", form) ?? form.getFieldValue("addressDetails");

  const [coordinates, setCoordinates] = useState<{
    latitude: string | undefined;
    longitude: string | undefined;
  }>({
    latitude,
    longitude,
  });
  const coordinatesValidation = useCoordinatesValidation(intl, form);
  const formattedLatitude = latitude && isFloat(latitude) ? floorToFixed(Number(latitude), 6) : 0;
  const formattedLongitude =
    longitude && isFloat(longitude) ? floorToFixed(Number(longitude), 6) : 0;

  const disableOkButton = areCoordinatesValid(latitude, longitude);

  const coordinatesText =
    !latitude && !longitude
      ? intl.formatMessage(messages.noCoordinates)
      : `${formattedLatitude}, ${formattedLongitude}`;

  const handleCancel = useCallback(() => {
    form.setFields([
      { errors: [], name: "latitude", value: coordinates.latitude },
      { errors: [], name: "longitude", value: coordinates.longitude },
    ]);
    setOpen(false);
  }, [form, coordinates]);

  const handleClick = useCallback(() => {
    handleCancel();
    if (isMapMode) {
      onEnterPinLocationMode?.({ entity });
      // move map to that entity's location
      if (hasLocation) {
        map.setCenter(convertGeoPointToLatLngLiteral(entity.geoPoint!));
        if (map.getZoom()! < 15) {
          map.setZoom(20);
        }
      }
    } else {
      onShowChangePinLocationModal();
    }
  }, [
    entity,
    handleCancel,
    hasLocation,
    isMapMode,
    map,
    onEnterPinLocationMode,
    onShowChangePinLocationModal,
  ]);

  const handleConfirm = useCallback(() => {
    onChange?.(true);
    setOpen(false);
    setCoordinates({ latitude, longitude });
    onReverseGeocodeAddress({
      callback: ({ address }: GeocodeResult) => {
        onSetCoordinatesChanged(true);
        const updatedAddress = geoAddressToAddress(address);
        const previousAddress = geoAddressToAddress(addressDetails);
        if (!isEqual(updatedAddress, previousAddress)) {
          form.setFields([{ name: "addressDetails", touched: false, value: updatedAddress }]);
          onSetAddressChanged(true);
        }
      },
      coordinates: [formattedLongitude, formattedLatitude],
    });
  }, [
    addressDetails,
    form,
    formattedLatitude,
    formattedLongitude,
    latitude,
    longitude,
    onChange,
    onReverseGeocodeAddress,
    onSetAddressChanged,
    onSetCoordinatesChanged,
  ]);

  const items: MenuProps["items"] = useMemo(
    () => [
      {
        key: "changeOnMap",
        label: intl.formatMessage(messages.changeOnMap),
        onClick: handleClick,
      },
      {
        key: "changeManually",
        label: intl.formatMessage(messages.changeManually),
        onClick: () => setOpen(true),
      },
    ],
    [handleClick, intl]
  );

  useEffect(() => {
    if (!formChanged) {
      setCoordinates({ latitude, longitude });
    }
  }, [formChanged, latitude, longitude]);

  return (
    <div>
      <Form.Item hidden name="latitude" />
      <Form.Item hidden name="longitude" />
      {addressChangedManually ? (
        <span className={styles.getCoordinates}>{intl.formatMessage(messages.getCoordinates)}</span>
      ) : (
        <Popconfirm
          cancelText={intl.formatMessage(messages.cancel)}
          description={
            <div className={styles.container}>
              <Alert message={intl.formatMessage(messages.alert)} showIcon type="info" />
              <Form.Item name="latitude" required={required} rules={coordinatesValidation}>
                <TextField disabled={disabled} label={intl.formatMessage(messages.latitude)} />
              </Form.Item>
              <Form.Item name="longitude" required={required} rules={coordinatesValidation}>
                <TextField disabled={disabled} label={intl.formatMessage(messages.longitude)} />
              </Form.Item>
            </div>
          }
          icon={null}
          okButtonProps={{ disabled: disableOkButton }}
          okText={intl.formatMessage(messages.ok)}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          open={open}
          overlayClassName={styles.popconfirm}
          placement="bottomRight"
        >
          <Dropdown menu={{ items }} trigger={disabled ? [] : ["click"]}>
            <Tooltip
              title={intl.formatMessage(messages.changeCoordinates)}
              trigger={disabled ? [] : "hover"}
            >
              {disabled ? (
                <span className={styles.disabled}>{coordinatesText}</span>
              ) : (
                <ButtonLink className={styles.buttonLink}>{coordinatesText}</ButtonLink>
              )}
            </Tooltip>
          </Dropdown>
        </Popconfirm>
      )}
    </div>
  );
};

const mapDispatchToProps = {
  onReverseGeocodeAddress: reverseGeocodeAddress,
};

export default connect(null, mapDispatchToProps)(AddressLabel);
